import type {
  FulfillmentOrderDetail,
  PackingRecordProduct,
  PackingScanLogEntry,
} from '@/infrastructure/FulfillmentAdminClient';
import { ARRIVED_LINE_STATUSES } from '@/utils/fulfillmentLineStatus';
import { isCancelledOrder } from '../viewOrders/runInbound';

/**
 * The desk's one shared code for a product with no barcode of its own (DY, 10/19): the supervisor looks at the
 * product, then scans this card once per unit, and it counts into the order's products that have no barcode.
 */
export const NO_BARCODE_CODE = 'SS-NOBARCODE';

/** The one row the box always holds besides the products, unless the order is a marketing send. */
export const SAMPLE_SET_KEY = '__SAMPLE_SET__';

/**
 * The sample envelope's code (DY, 10/22): the packer scans it instead of pressing Put in. Until the envelopes carry it,
 * the desk prints the card on this screen and sticks it on them. Like SS-NOBARCODE it is a reserved desk code, read
 * before any product barcode, so it must never be registered as a product's barcode.
 */
export const SAMPLE_SET_CODE = 'SS-SAMPLE';

/** What the catalog says about one sku; only the fields this screen reads. */
export interface PackingCatalogEntry {
  barcode?: string;
  name?: string;
  nameKor?: string;
  size?: string;
  productImageUrls?: string[];
}

/** One thing the packer has to put in the box: a sku (every line of it together) or the sample set. */
export interface CheckRow {
  key: string;
  sku: string | null;
  lineNos: number[];
  name: string;
  size: string | null;
  imageUrl: string | null;
  /** The catalog barcode; null when none is registered, or when the catalog could not be read. */
  barcode: string | null;
  expected: number;
  scanned: number;
  sampleSet: boolean;
  /** A line of it has not been inbounded, so the goods may not be on the desk yet. */
  notArrived: boolean;
}

/** Something about the order the packer must see before packing; `bad` means do not pack. */
export interface OrderNotice {
  tone: 'bad' | 'warn';
  text: string;
}

const LINE_CANCELLED = 'CANCELLED';

/** The physical code as compared: no surrounding whitespace, letters folded, since a Korean-mode scanner upper-cases them. */
export const codeKey = (code: string): string => code.trim().toUpperCase();

/**
 * The PACKING sheet's barcode is the bare order id (Code128): six or seven digits for a store order, ten for an
 * order made in the admin (marketing, clone, 2.0-native, numbered from 1,000,000,000). Product codes are eight,
 * twelve, thirteen or fourteen digits (EAN-8, UPC-A, EAN-13, ITF-14) and catalog skus nine, so none of them
 * switches the order on screen.
 */
export const looksLikeOrderNumber = (code: string): boolean => /^(?:\d{6,7}|\d{10})$/.test(code.trim());

/**
 * The rows the packer has to fill, by the rule the PACKING sheet prints by (Facade `GetInternalInvoiceHandler`):
 * every line except a cancelled one and the sample-assignment lines, counted in physical units
 * (`totalQuantity`), then one sample set unless the order is a marketing send. Lines of one sku fold into one
 * row, since the scan cannot tell which line a unit belongs to.
 */
export const buildCheckRows = (
  detail: FulfillmentOrderDetail,
  catalog: Readonly<Record<string, PackingCatalogEntry>>,
): CheckRow[] => {
  const bySku = new Map<string, CheckRow>();
  const packed = [...detail.lines]
    .sort((a, b) => a.lineNo - b.lineNo)
    .filter((line) => line.lineStatus !== LINE_CANCELLED && !line.isSample);
  for (const line of packed) {
    const entry = catalog[line.sku];
    const arrived = ARRIVED_LINE_STATUSES.includes(line.lineStatus);
    const existing = bySku.get(line.sku);
    if (existing) {
      existing.lineNos.push(line.lineNo);
      existing.expected += line.totalQuantity;
      existing.notArrived = existing.notArrived || !arrived;
      continue;
    }
    bySku.set(line.sku, {
      key: line.sku,
      sku: line.sku,
      lineNos: [line.lineNo],
      name: line.productNameKr ?? entry?.nameKor ?? line.productName ?? entry?.name ?? line.sku,
      size: line.size ?? entry?.size ?? null,
      imageUrl: line.imageUrl ?? entry?.productImageUrls?.[0] ?? null,
      barcode: entry?.barcode?.trim() ? entry.barcode.trim() : null,
      expected: line.totalQuantity,
      scanned: 0,
      sampleSet: false,
      notArrived: !arrived,
    });
  }
  const rows = [...bySku.values()];
  if (!detail.header.isMarketing) {
    rows.push({
      key: SAMPLE_SET_KEY,
      sku: null,
      lineNos: [],
      name: 'Sample set',
      size: null,
      imageUrl: null,
      barcode: null,
      expected: 1,
      scanned: 0,
      sampleSet: true,
      notArrived: false,
    });
  }
  return rows;
};

/** What stops or should slow the packer, worst first. */
export const orderNotices = (detail: FulfillmentOrderDetail, rows: readonly CheckRow[]): OrderNotice[] => {
  const notices: OrderNotice[] = [];
  if (isCancelledOrder(detail.header)) {
    notices.push({ tone: 'bad', text: 'This order is cancelled. Do not pack it.' });
  }
  if (detail.header.holdReason) {
    notices.push({
      tone: 'bad',
      text: `On hold: ${detail.header.holdReason}. Do not pack it until the hold is lifted.`,
    });
  }
  const waiting = rows.filter((row) => row.notArrived).length;
  if (waiting > 0) {
    notices.push({
      tone: 'warn',
      text: `${waiting} product${waiting === 1 ? ' is' : 's are'} not inbounded yet. Inbound ${waiting === 1 ? 'it' : 'them'} on View Orders before packing.`,
    });
  }
  if (!rows.some((row) => !row.sampleSet)) {
    notices.push({ tone: 'bad', text: 'No product of this order goes in the box.' });
  }
  return notices;
};

/**
 * Every product row a scanned code names: its catalog barcode, or its sku typed or printed as is. More than one
 * means two products of the order carry the same barcode, and the scan cannot tell which one is in hand.
 */
export const rowsByCode = (rows: readonly CheckRow[], code: string): CheckRow[] => {
  const key = codeKey(code);
  return rows.filter(
    (row) =>
      !row.sampleSet && ((row.barcode !== null && codeKey(row.barcode) === key) || codeKey(row.sku ?? '') === key),
  );
};

/** Rows whose barcode another row of the order also carries; they are counted by hand, never by the scan. */
export const sharedBarcodeKeys = (rows: readonly CheckRow[]): Set<string> => {
  const byCode = new Map<string, string[]>();
  for (const row of rows) {
    if (!row.sampleSet && row.barcode !== null) {
      const key = codeKey(row.barcode);
      byCode.set(key, [...(byCode.get(key) ?? []), row.key]);
    }
  }
  return new Set([...byCode.values()].filter((keys) => keys.length > 1).flat());
};

export type CountResult = { kind: 'counted'; row: CheckRow } | { kind: 'full'; row: CheckRow } | { kind: 'missing' };

/** Adds one unit to a row; a row already holding every unit refuses, so an extra unit is never counted. */
export const countOne = (rows: readonly CheckRow[], key: string): { rows: CheckRow[]; result: CountResult } => {
  const target = rows.find((row) => row.key === key);
  if (!target) {
    return { rows: [...rows], result: { kind: 'missing' } };
  }
  if (target.scanned >= target.expected) {
    return { rows: [...rows], result: { kind: 'full', row: target } };
  }
  const counted = { ...target, scanned: target.scanned + 1 };
  return { rows: rows.map((row) => (row.key === key ? counted : row)), result: { kind: 'counted', row: counted } };
};

/** Takes one unit back off a row, for an undo. */
export const uncountOne = (rows: readonly CheckRow[], key: string): CheckRow[] =>
  rows.map((row) => (row.key === key && row.scanned > 0 ? { ...row, scanned: row.scanned - 1 } : row));

export const unitsOf = (rows: readonly CheckRow[]): { scanned: number; expected: number } =>
  rows.reduce((sum, row) => ({ scanned: sum.scanned + row.scanned, expected: sum.expected + row.expected }), {
    scanned: 0,
    expected: 0,
  });

/** Every unit of every row is in. An order with nothing to pack is never complete. */
export const isComplete = (rows: readonly CheckRow[]): boolean =>
  rows.some((row) => !row.sampleSet) && rows.every((row) => row.scanned >= row.expected);

/**
 * The order's products with no barcode registered, and those of them still short of units. The shared card counts
 * one unit per scan into the first short one, so two units take two scans.
 */
export const noBarcodeRows = (rows: readonly CheckRow[]): { all: CheckRow[]; short: CheckRow[] } => {
  const all = rows.filter((row) => !row.sampleSet && row.barcode === null);
  return { all, short: all.filter((row) => row.scanned < row.expected) };
};

/** How one unit was counted: its own barcode, the supervisor's eye on its row, or the shared no-barcode card. */
export type CountMethod = 'SCAN' | 'EYE' | 'CARD';

/** A label code as compared: no whitespace anywhere (a scanner or a paste can add some), letters folded. */
const labelKey = (code: string): string => code.replace(/\s+/g, '').toUpperCase();

/**
 * The order's carrier labels (SSD-7587): every outbound tracking row's number and the carrier's own shipment id, since
 * a label can carry either as its main barcode (a YunExpress label prints the YT waybill, Deleo its order id).
 */
export const outboundLabelCodes = (detail: FulfillmentOrderDetail): string[] =>
  detail.tracking
    .filter((row) => row.kind === 'OUTBOUND')
    .flatMap((row) => [row.number, row.carrierOrderId ?? ''])
    .map((code) => code.trim())
    .filter((code, index, all) => code !== '' && all.indexOf(code) === index);

/** The order's own label number the scanned code is, as the tracking row holds it; null when it is none of them. */
export const matchedLabel = (detail: FulfillmentOrderDetail, code: string): string | null => {
  const key = labelKey(code);
  return key === '' ? null : (outboundLabelCodes(detail).find((label) => labelKey(label) === key) ?? null);
};

/** The scanned code is one of the order's carrier labels, read off the box on the desk. */
export const isOrderLabel = (detail: FulfillmentOrderDetail, code: string): boolean =>
  matchedLabel(detail, code) !== null;

/** What the record says about each row: the units it took, the units counted, and how many of them by eye. */
export const recordProducts = (
  rows: readonly CheckRow[],
  counted: readonly string[],
  countedBy: readonly CountMethod[],
): PackingRecordProduct[] =>
  rows.map((row) => ({
    key: row.key,
    name: row.name.slice(0, 200),
    expected: row.expected,
    scanned: row.scanned,
    byEye: counted.filter((key, index) => key === row.key && countedBy[index] !== 'SCAN').length,
  }));

/** The server keeps at most this many scans per record. */
export const SCAN_LOG_LIMIT = 500;

/** Adds one entry; past the limit the oldest go, except the first, which says how the order was opened. */
export const appendScanLog = (
  log: readonly PackingScanLogEntry[],
  entry: PackingScanLogEntry,
): PackingScanLogEntry[] => {
  const next = [...log, entry];
  return next.length <= SCAN_LOG_LIMIT ? next : [next[0]!, ...next.slice(next.length - SCAN_LOG_LIMIT + 1)];
};

/** A record id (a v4 uuid) for one box; the browser's own maker where it has one. */
export const newRecordId = (): string => {
  const maker = window.crypto as (Crypto & { randomUUID?: () => string }) | undefined;
  if (maker?.randomUUID) {
    return maker.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (maker?.getRandomValues) {
    maker.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
